﻿// fetchWithAuth, role helpers, escape, status
//
// `currentAuthUser` is declared as a global in 01-state.ts and shared across
// all script-tag-loaded files. We re-declare it here as `var` to satisfy
// TypeScript's per-file checker without producing a runtime conflict
// (multiple top-level `var` decls of the same name are legal in script mode
// and refer to the same binding).

interface AdminFetchResult {
  ok: boolean;
  status: number;
  data: any;
}

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const resp = await fetch(url, { ...options, credentials: "same-origin" });
  if (resp.status === 401) {
    window.location.href = "/login";
    throw new Error("Not authenticated");
  }
  return resp;
}

async function fetchAdmin(path: string, options: RequestInit = {}): Promise<AdminFetchResult> {
  const headers: Record<string, string> = { ...(options.headers as Record<string, string> | undefined) };
  const body = options.body;
  if (body && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
  const resp = await fetch(path, { ...options, headers, credentials: "same-origin" });
  const text = await resp.text();
  let data: any = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch (_) {
    data = { raw: text };
  }
  return { ok: resp.ok, status: resp.status, data };
}

function isAdminUser(): boolean {
  const role = currentAuthUser?.role;
  return role === "platform-admin" || role === "owner" || role === "admin";
}

// Mirror of backend permission helpers in src/auth.ts. Keep in sync.
function isPlatformAdminRole(): boolean {
  return currentAuthUser?.role === "platform-admin";
}
function canManageBillingRole(): boolean {
  const r = currentAuthUser?.role;
  return r === "platform-admin" || r === "owner";
}
function canManageDataRole(): boolean {
  // Anyone except member (and disabled / signed-out users).
  const r = currentAuthUser?.role;
  return r === "platform-admin" || r === "owner" || r === "admin";
}
function canViewBillingDataRole(): boolean {
  return canManageDataRole();
}
function isMemberRole(): boolean {
  return currentAuthUser?.role === "member";
}

function isTabVisibleForCurrentBillingModel(tabName: string): boolean {
  const company = (window as any).currentCompany || {};
  const model = (company.billingModel === "service_based" || company.businessType === "field_service")
    ? "service_based"
    : "hourly";
  const visibleByModel: Record<string, string[]> = {
    hourly: ["dashboard", "clients", "projects", "tools", "reports", "settings", "admin"],
    service_based: ["dashboard", "clients", "projects", "schedule", "tools", "settings", "admin"],
  };
  return (visibleByModel[model] || visibleByModel.hourly).includes(tabName);
}

/**
 * Show/hide tabs and write controls based on the current user's role.
 * Called after auth loads (loadCurrentUser) and on logout.
 *   - member: sees only Home (time tracking) + read-only Clients/Projects/Calendar
 *   - admin/owner/platform-admin: sees everything they're allowed to manage
 */
function applyRoleVisibility(): void {
  const member = isMemberRole();

  // Tab buttons that members shouldn't see at all.
  const memberHiddenTabs = ["tools", "reports", "settings"];
  const billingHiddenTabs = Object.keys(TAB_META).filter((tab) => !isTabVisibleForCurrentBillingModel(tab));
  document.querySelectorAll<HTMLElement>(".tab-nav .tab-btn[data-tab]").forEach((btn) => {
    const tab = btn.getAttribute("data-tab") || "";
    const hideForRole = member && memberHiddenTabs.includes(tab);
    const hideForBillingModel = billingHiddenTabs.includes(tab);
    if (hideForRole || hideForBillingModel) {
      btn.style.display = "none";
    } else if (tab !== "admin") {
      // adminTabBtn visibility is managed by adminInitPanel()
      btn.style.display = "";
    }
  });

  // If a member is currently viewing a hidden tab, bounce them home.
  if ((member && memberHiddenTabs.includes(currentView)) || billingHiddenTabs.includes(currentView)) {
    switchTab("dashboard");
  }

  // Add or remove a body-level class so CSS can hide member-restricted controls
  // (e.g. "Add Client" form, "Edit Project" buttons) without per-element JS.
  // Note: role-admin is the *admin* role specifically (not owner/platform).
  // Used to disable billing controls for admins while keeping them visible.
  document.body.classList.toggle("role-member", member);
  document.body.classList.toggle("role-admin", currentAuthUser?.role === "admin");
  document.body.classList.toggle("role-owner", currentAuthUser?.role === "owner");
  document.body.classList.toggle("role-platform", currentAuthUser?.role === "platform-admin");
}

function adminSetStatus(id: string, message: string, isError = false): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.textContent = message || "";
  (el as HTMLElement).style.color = isError ? "#dc2626" : "";
}

function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/g, (ch) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[ch] as string));
}

function escapeHtmlAttr(value: unknown): string {
  return escapeHtml(value);
}

function escapeJsSingleQuoted(value: unknown): string {
  return String(value ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/'/g, "\\'");
}
